import React, { useEffect } from "react"
import NavMain from "../../app/NavMain"
import { Helmet } from "react-helmet-async"
import { useInterval } from "../timing/use-interval"
import ScrollablePage from "../../ui/ScrollablePage"
import Layout from "../../ui/Layout"
import Stack from "../../ui/Stack"
import { useDispatch, useSelector } from "react-redux"
import Paper from "../../ui/Paper"
import DateTimeFilter from "../filters/DateTimeFilter"
import styled from "styled-components"
import ErrorMsg from "../../ui/ErrorMsg"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import SolveStatusPie from "./SolveStatusPie"
import { fetchObservationsStats, selectObservationStats } from "./slice"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { updateSearchParamsWithFilters } from "../filters/filters"
import { parseObservationsStatsFilters } from "./filters"
import CurationOutcomesPie from "./CurationOutcomesPie"
import OverviewBarChart from "./OverviewBarChart"
import SolverStatusBarChart from "./SolverStatusBarChart"
import PropTypes from "prop-types"

import {
  fetchAstrositeStates,
  selectAstrositesById,
  selectAstrositeStatesByContext,
} from "../astrosites/slice"
import MapPinLabel from "../geography/MapPinLabel"
import AstrositeName from "../astrosites/AstrositeName"
import AstrositeStatus from "../astrosites/AstrositeStatus"
import { selectAdvancedMode } from "../settings/slice"
import AstrositeDiskUsage from "./AstrositeDiskUsage"
import DropDownFilter from "../filters/DropDownFilter"
import { Header, Overview } from "../../ui/PageHeader"
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline"

const TopSection = styled.div`
  display: grid;
  gap: 1rem 1rem;
  grid-template-columns: 2fr 2fr;
  margin-bottom: 1rem;
`

const MapSection = styled.div`
  display: flex;
  gap: 1rem 1rem;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 1rem;
`

const PieSection = styled.div`
  display: grid;
  gap: 1rem 1rem;
  grid-template-columns: 1.5fr 1.5fr 1.5fr;
  margin-bottom: 1rem;
`

const FilterContainer = styled.div`
  grid-column-end: span 1;
`

const AstroSelectContainer = styled.div`
  width: 100%;
  select {
    width: 100%;
  }
`

const Dashboard = () => {
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const astrositesById = useSelector(selectAstrositesById)
  // We get the full objects from the ids that match
  const observationsStats = useSelector((state) =>
    selectObservationStats(state)
  )

  // Parse parameters into filter object (which drives filters UI state)
  const location = useLocation()

  const navigate = useNavigate()
  const urlParams = new URLSearchParams(location.search)

  const filter = parseObservationsStatsFilters(urlParams)
  updateSearchParamsWithFilters(urlParams, filter)
  useEffect(() => {
    if (!searchParams.get("from") || !searchParams.get("to")) {
      setSearchParams({ from: "m7d", to: "now" })
    }

    dispatch(
      fetchObservationsStats({
        filter,
      })
    )
  }, [urlParams.toString()])

  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const setFilter = (filter = {}) => {
    const updUrlParams = updateSearchParamsWithFilters(urlParams, filter)
    navigate({
      pathname: location.pathname,
      search: `?${updUrlParams}`,
    })
  }

  const onAstrositeChanged = (e) => {
    const newValue = e.target.value
    setFilter({
      ...filter,
      astrosite_id: newValue,
    })
  }

  // Poll at an interval, based on whether "Update live" is checked
  // As a hack, for non-live updates, set a long (but non-infinite, which breaks) interval, e.g. a week
  useInterval(
    () => {
      dispatch(
        fetchObservationsStats({
          filter,
        })
      )
    },
    20000,
    false
  )

  return (
    <Layout>
      <NavMain key="nav-left" />
      <Stack>
        {!observationsStats?.data &&
          observationsStats?._loadStatus === "loading" && (
            <Backdrop>
              <LoadingSpinner />
            </Backdrop>
          )}
        {observationsStats?._loadStatus === "rejected" && (
          <ErrorMsg
            msg={`there was a problem loading the dashboard : ${observationsStats?._errorMessage}`}
          />
        )}
        <ScrollablePage>
          <Helmet>
            <title>Dashboard - Astrosite</title>
          </Helmet>
          <Header>
            <Overview>
              <h1>Analytics Dashboard</h1>
              <p>
                {observationsStats?._loadStatus == "loading" && (
                  <span>
                    <LoadingSpinnerInline /> loading...
                  </span>
                )}
              </p>
            </Overview>
          </Header>
          <TopSection>
            <FilterContainer>
              <span>Astrosite</span>
              <AstroSelectContainer onChange={onAstrositeChanged}>
                <DropDownFilter
                  values={Object.keys(astrositesById).map((a) => ({
                    name: a,
                    value: a,
                  }))}
                  filterByName="astrosite_id"
                  filter={filter}
                  setFilter={setFilter}
                  active={filter.astrosite_id ? true : false}
                />
              </AstroSelectContainer>
            </FilterContainer>
            <FilterContainer>
              <span>Date range</span>
              <DateTimeFilter filter={filter} setFilter={setFilter} />
            </FilterContainer>
          </TopSection>
          <PieSection>
            <Paper>
              <CurationOutcomesPie
                curationResults={observationsStats?.data?.count_curation}
              />
            </Paper>
            <Paper>
              <OverviewBarChart
                stats={observationsStats?.data?.observation_stats}
              />
            </Paper>
            <Paper>
              <SolveStatusPie
                solveCounts={observationsStats?.data?.solve_status}
              />
            </Paper>
          </PieSection>
          <TopSection>
            <Paper>
              <SolverStatusBarChart
                solveStatusByOrbitType={
                  observationsStats?.data?.solve_status_by_orbit_regime
                }
              />
            </Paper>
            <Paper>
              <SolverStatusBarChart
                solveStatusByOrbitType={
                  observationsStats?.data?.solve_status_by_orbit_regime
                }
                usePercent={true}
              />
            </Paper>
          </TopSection>
          <TopSection>
            {astrositesById &&
              Object.keys(astrositesById)
                ?.filter((id) => {
                  // only show the disk usage for the matching astrosite if an astrosite_id filter is set
                  if (!filter.astrosite_id) {
                    return id
                  }
                  return filter.astrosite_id == id
                })
                .map((id) => (
                  <Paper key={id}>
                    <AstrositeDiskUsage astrositeID={id} />
                  </Paper>
                ))}
          </TopSection>
          <MapSection>
            <AstrositesStatusesMap />
          </MapSection>
        </ScrollablePage>
      </Stack>
    </Layout>
  )
}

const AstrositesStatusesMap = () => {
  const dispatch = useDispatch()

  const astrositesById = useSelector(selectAstrositesById)
  // we get any matching states for this astrosite
  const astrositeStateMatches = useSelector((state) =>
    selectAstrositeStatesByContext(state, `dashboard`)
  )

  useEffect(() => {
    // We want to make sure we get results for all the astrosites even if some are offline so we must do sep calls per site.
    Object.keys(astrositesById) &&
      Object.keys(astrositesById).forEach((site) => {
        dispatch(
          fetchAstrositeStates({
            filter: {
              astrosite_id: site,
            },
            page: {
              offset: 0,
              limit: 50,
            },
            context: "root",
          })
        )
      })
  }, [])

  let astrositeLocations =
    astrositesById &&
    Object.values(astrositesById)
      .map((astrosite) => {
        return (
          astrosite.location && {
            id: astrosite.id,
            content: <AstrositesStatusesMapLabel astrosite={astrosite} />,
            latitude: astrosite.location.latitude_degrees,
            longitude: astrosite.location.longitude_degrees,
          }
        )
      })
      .filter((location) => location)

  return astrositeStateMatches?._loadStatus !== "loading" &&
    astrositeStateMatches?._loadStatus === "rejected" ? (
    <ErrorMsg msg={"There is a problem fetching the data"} showIcon={true} />
  ) : (
    astrositeStateMatches?._loadStatus !== "loading" &&
      astrositeLocations.length > 0 && (
        <MapPinLabel
          pins={astrositeLocations}
          width={"100%"}
          height={"28rem"}
        />
      )
  )
}

const AstrositesStatusesMapLabel = ({ astrosite = {} }) => {
  const advancedMode = useSelector(selectAdvancedMode)
  return (
    <div>
      <AstrositeName astrositeId={astrosite.id} />
      <AstrositeStatus
        astrosite={astrosite}
        advancedMode={advancedMode}
        hideLastUpdated={true}
      />
    </div>
  )
}

AstrositesStatusesMapLabel.propTypes = {
  astrosite: PropTypes.object.isRequired, // details about the astrosite
}

export default Dashboard
